import LoadingSkeleton from "./loading_skeleton.tsx";
import { SecondaryBody } from "@sprout/typography.tsx";

import PreviewIcon from "@sprout/icons/preview.tsx";
import { useMemo, useState } from "react";
import useTranslation from "@hooks/use_translation.ts";
import { differenceInCalendarDays } from "date-fns";
import { SecondaryButtonLink } from "@sprout/buttons";
import { router } from "@inertiajs/react";

type Props = {
  loading?: boolean;
  publishedAt: string;
  url: string;
  external: boolean;
  trackClick?: () => void;
  viewed: boolean;
  onView: () => void;
};
const Footer = ({ loading, publishedAt, url, external, trackClick, viewed, onView }: Props) => {
  const { t } = useTranslation("jobSearch");
  const [previewIconColor, setPreviewIconColor] = useState("green");

  const timeSincePublished = useMemo((): string | null => {
    const datePublished = Date.parse(publishedAt);

    if (isNaN(datePublished)) {
      return "";
    }

    const daysSince = differenceInCalendarDays(new Date(), datePublished);
    if (daysSince < 7) {
      return t("publishedDaysAgo", { count: daysSince });
    }

    const weeksSince = Math.floor(daysSince / 7);
    return t("publishedWeeksAgo", { count: weeksSince });
  }, [publishedAt, t]);

  return loading ? (
    <LoadingSkeleton className="h-4 w-[80px]" />
  ) : (
    <>
      <SecondaryBody>
        {timeSincePublished}
        {viewed && ` • ${t("viewed")}`}
      </SecondaryBody>
      <SecondaryButtonLink
        href={url}
        onClick={(e) => {
          trackClick?.();
          if (!viewed) {
            onView();
          }
          if (!external) {
            e.preventDefault();
            fetch(url, {
              headers: { "X-Modal-Request": "true" },
              credentials: "same-origin",
            })
              .then((r) => (r.ok ? r.json() : Promise.reject()))
              .then((data) => {
                router.replaceProp("jobPost", data.jobPost);
                router.replaceProp("boardConfiguration", data.boardConfiguration);
                router.replaceProp("submitPath", data.submitPath);
                router.replaceProp("jbenHost", data.jbenHost);
                router.replaceProp("urlToken", data.urlToken);
              })
              .catch(() => {
                router.visit(url, { preserveScroll: true });
              });
          }
        }}
        external={external}
        size="medium"
        onMouseEnter={() => setPreviewIconColor("dark-green")}
        onMouseLeave={() => setPreviewIconColor("green")}
      >
        <div className="flex flex-row gap-1 items-center justify-between">
          {t("viewJob")}
          <PreviewIcon color={previewIconColor} size="sm" />
        </div>
      </SecondaryButtonLink>
    </>
  );
};

export default Footer;
